import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import '../styles/Collection.css';
import { Container } from 'react-bootstrap';
import Breadcrumbs from './Breadcrumbs';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import SearchPageProductList from './SearchPageProductList';
import PageNavCurPageIndicator from './PageNavCurPageIndicator';
import PageNav from './PageNav';
import { LoadingContext } from './context/LoadingContext';
import SEO from './SEO';
import { bool } from 'prop-types';
import NotFound from './NotFound';
import AppContext from './context/AppContext';
import hasStoreName from '../helpers/hasStoreName';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

export default function CollectionVendors({ isMobile }) {
    const { appContext } = useContext(AppContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const collectionBasePath = collectionPath[0].pathBeforeParams;
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchLoaded, setSearchLoaded] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [productList, setProductList] = useState([]);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    /* Start search params */
    const vendor = searchParams.get('q');
    const page = searchParams.get('page');
    /* End search params */
    /* Pagination code */
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const handlePageChange = (newCurPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', parseInt(newCurPage));
        setSearchParams(newSearchParams);
    };
    const {
        curPage,
        curRange,
        nextRange,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: Math.min(parseInt(searchParams.get('page') || 0, 0)),
        onUpdate: handlePageChange,
    });
    /* End pagination code */
    // Product fetching func.
    const handleSearch = useCallback(async () => {
        try {
            if (!vendor)
                return;
            startLoading();
            setSearchLoading(true);
            const params = new URLSearchParams({
                v: vendor,
                page: page || 0,
            }).toString();
            // Products
            const productResponse = await fetch(new URL('api/products/filter?' + params, process.env.APP_URL));
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                });
            }
            // Update Search
            setSearchLoaded(true);
            setSearchLoading(false);
            finishLoading();
        } catch (e) {
            console.error(e);
            setSearchLoaded(false);
        }
    }, [vendor, page]);
    useEffect(() => {
        handleSearch();
    }, [handleSearch]);
    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!searchLoaded
                || !productList?.length)
            return '';
        let title = vendor;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [searchLoaded, productList]);
    return (<Container
        style={{ padding: '0' }}
        fluid>
        <div
            className={'page--section'}>
            {(searchLoaded === true && productList !== null) && (<>
                {(!productList.length || productList.at(0)?.vendor !== vendor) && (<NotFound />)}
                {(productList.length > 0 && productList.at(0)?.vendor === vendor) && (<>
                    <SEO
                        seoTitle={title}
                        seoDescription={`Explore our collection of products that match vendor "${vendor}".`}
                        canonicalUrl={new URL(`${collectionBasePath}/vendors?q=${vendor}`, process.env.APP_URL).toString()}
                        breadsArr={[
                            {
                                title: 'Home',
                                handle: '/',
                            },
                            {
                                title: 'Collections',
                                handle: `${collectionBasePath}/`,
                            },
                            {
                                title: vendor,
                                handle: `${collectionBasePath}/vendors?q=${vendor}`,
                            },
                        ]}
                    />
                    <div
                        className={'collection--heading'}>
                        <Breadcrumbs
                            breadcrumbList={[
                                [
                                    vendor,
                                    `${collectionBasePath}/vendors?q=${vendor}`,
                                ],
                            ]} />
                        <h1
                            className={'collection--title'}>{vendor}</h1>
                    </div>
                    <div
                        className={'search-page--inner'}>
                        <SearchPageProductList
                            ref={containerRef}
                            isMobile={isMobile}
                            productList={productList}
                            searchLoading={searchLoading}>
                            <PageNavCurPageIndicator
                                curRange={curRange}
                                nextRange={nextRange}
                                totalCount={pagesData.count}
                                resourceType={'products'}
                                noStyle={true}
                            />
                        </SearchPageProductList>
                    </div>
                    {productList.length > 0 && (<PageNav
                        curPage={curPage}
                        totalPages={pagesData.pages}
                        generatedPagesData={generatedPagesData}
                        paginateUser={paginateUser}
                    />)}
                </>)}
            </>)}
        </div>
    </Container>);
}

CollectionVendors.propTypes = {
    isMobile: bool,
};